/**
 * Página MOBILE "Resumo" (Task #228 / #31 + Fase 1 03/07 — painel do sócio).
 *
 * MÁXIMO de info na palma: 1 card por ÁREA, cada um com o número-chave, tudo
 * dos MESMOS hooks/endpoints do desktop (sem backend novo):
 *  - Saldo em conta ....... useSaldoV2 (+ breakdown por banco em disclosure)
 *  - Fluxo 30d ............ useFluxoV2 + computeRows30d/_menorSaldo30d (menor
 *                           saldo projetado) + saldo pós-6DU — TOCÁVEL → Fluxo
 *  - Próximo 6DU .......... useProximo6du + getFluxo→kpis6DuFromApi +
 *                           usePosicaoAberta + /api/recebimentos-6du (#15,
 *                           total esperado por contraparte) — TOCÁVEL → Fluxo
 *  - Resultado do mês ..... /api/v2/dre (useDreV2: Receita líq/EBITDA/Lucro)
 *  - Despesas do mês ...... mesmas linhas D do DRE (total + maior linha)
 *  - A receber ............ /api/contas-receber (useContasReceber: total +
 *                           cronograma d0-30/31-60/61-90/90+)
 *  - Posição/Exposição .... /api/exposicao (MtM líquido + maior contraparte)
 *  - PLD .................. usePldProjecao — TOCÁVEL → PLD
 *
 * TOQUE: card navega SÓ onde já existe tela mobile (Fluxo, PLD). DRE/Despesas/
 * A receber/Exposição são read-only (drill = Fase 2) — sem botão morto (#246).
 * Erro por card = ErroNote explícito (≠ carregando "…" ≠ sem dado "—");
 * ATUALIZAR no header invalida caches e remonta (MobileShell).
 * Valores ABREVIADOS (mi/mil) como os KPIs desktop; breakdown por banco cheio.
 */
import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { getExposicao, getFluxo, getRecebimentos6du } from "../../api/client";
import type {
  ExposicaoResponse,
  FluxoRow,
  Recebimentos6duResponse,
} from "../../api/types";
import { computeRows30d, _menorSaldo30d } from "../financeiro/FluxoBarChart";
import { useCalendarioCaixa } from "../../hooks/useCalendarioCaixa";
import { useContasReceber } from "../../hooks/useContasReceber";
import { useDreV2 } from "../../hooks/useDreV2";
import { useFluxoV2 } from "../../hooks/useFluxoV2";
import { usePldProjecao } from "../../hooks/usePldProjecao";
import { usePosicaoAberta } from "../../hooks/usePosicaoAberta";
import { useProximo6du } from "../../hooks/useProximo6du";
import { useSaldoV2 } from "../../hooks/useSaldoV2";
import { formatarDataLongaBr, ymdIso } from "../../utils/dateBr";
import { fmtBRL } from "../../utils/formatters";
import { kpis6DuFromApi } from "../../utils/mockData";
import { fmtPld, mesRefCorrente, mesLabel } from "../mesa/pld/pldShared";
import type { MobileTab } from "./MobileShell";
import { Card, ErroNote, MONEY, MONEY_ABBR, MobileHeader, Stat, sign } from "./mobileUi";
import { useFetchLocal } from "./useFetchLocal";

// Janela de fetch do fluxo: cobre o próximo 6DU (≤ ~1 mês à frente) com folga.
function janela6du(hoje = new Date()): { inicio: string; fim: string } {
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return {
    inicio: iso(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)),
    fim: iso(new Date(hoje.getFullYear(), hoje.getMonth() + 3, 0)),
  };
}

// useFetchLocal extraído pra ./useFetchLocal no #25a (MobilePnl reusa).

export function MobileResumo({ onLogout, onRefresh, onNavigate }: {
  onLogout: () => void;
  onRefresh?: () => void;
  onNavigate?: (tab: MobileTab) => void;
}) {
  const fluxoRows = useFetchLocal<FluxoRow[]>(() => {
    const { inicio, fim } = janela6du();
    return getFluxo(inicio, fim).then((r) => r.rows); // #60: envelope
  });
  const receb6du = useFetchLocal<Recebimentos6duResponse>(() => getRecebimentos6du());
  const exposicao = useFetchLocal<ExposicaoResponse>(() => getExposicao());

  const saldoV2 = useSaldoV2();
  const proximo6du = useProximo6du();
  const fluxoV2 = useFluxoV2();
  const mesRef6du = proximo6du.info?.mes_referencia ?? null;
  const posAberta = usePosicaoAberta(mesRef6du);
  const pld = usePldProjecao(mesRefCorrente(), "SE");
  const dre = useDreV2({ regime: "competencia", periodo: "mensal", mesRef: mesRefCorrente() });
  const cr = useContasReceber();

  // ---- Saldo atual — SEM fallback mock em NENHUM numero exibido (review
  // 03/07: o mock vazava pro card Fluxo 30d via saldoInicial). ----
  const [saldoAberto, setSaldoAberto] = useState(false);
  const saldoReal = saldoV2.data?.saldo_total_atual ?? null;
  const contasMonitoradas = (saldoV2.data?.contas ?? []).filter((c) => c.monitorada);
  const nomesNa = (saldoV2.data?.contas ?? []).filter((c) => !c.monitorada).map((c) => c.banco_nome);

  // ---- Próximo 6DU ----
  // 05/08 FONTE ÚNICA (paridade com o desktop): E/S do 6DU vêm do Calendário
  // de Caixa LIVE — o /api/fluxo legado (precompute 1×/dia) apodrecia intradia.
  const officialDate = proximo6du.info?.data ?? null;
  const calCaixa = useCalendarioCaixa();
  const kpis6du = useMemo(() => {
    if (officialDate) {
      const d = calCaixa.data.dias.find((x) => x.eh_6du_atual);
      if (d && d.data === ymdIso(officialDate)) {
        const entradas = Number(d.entra);
        const saidas = Math.abs(Number(d.sai));
        return { entradas, saidas, liquido: entradas - saidas };
      }
    }
    return fluxoRows.data && officialDate ? kpis6DuFromApi(fluxoRows.data, officialDate) : null;
  }, [fluxoRows.data, officialDate, calCaixa.data]);
  const temDados6du = kpis6du !== null;
  const posValor = Number(posAberta.data?.total_valor_mtm_estimado ?? "0");
  const posTemDados = !posAberta.isLoading && (posAberta.data?.linhas_posicao_aberta?.length ?? 0) > 0;
  const saldoTotal6du = (kpis6du ? kpis6du.entradas - kpis6du.saidas : 0) + (posTemDados ? posValor : 0);
  const temAlgum6du = temDados6du || posTemDados;

  // ---- Fluxo 30d: menor saldo projetado (mesma derivação do desktop:
  // computeRows30d + _menorSaldo30d sobre as MESMAS séries do gráfico) ----
  const menorSaldo30d = useMemo(() => {
    // saldoReal null (erro/carregando) -> null: nunca ancorar no mock.
    if (!fluxoV2.data || saldoReal === null) return null;
    const cpOk = fluxoV2.data.cp_disponivel;
    const rows30 = computeRows30d(
      {
        entradasOmie: fluxoV2.data.entradas_omie_dia,
        saidasOmie: cpOk ? fluxoV2.data.saidas_cp_dia : fluxoV2.data.saidas_omie_dia,
        energiaNet: fluxoV2.data.energia_net_dia ?? [],
        posicaoAberta6du: fluxoV2.data.posicao_aberta_6du_dia ?? [],
      },
      undefined,
      fluxoV2.data.hoje,
      saldoReal,
      30,
    );
    return _menorSaldo30d(rows30);
  }, [fluxoV2.data, saldoReal]);
  const saldoPos6du = saldoReal !== null ? saldoReal + (temAlgum6du ? saldoTotal6du : 0) : null;

  // ---- Resultado / Despesas do mês (DRE gerencial, mês corrente) ----
  const calc = (codigo: string) =>
    dre.data?.calculadas.find((c) => c.codigo === codigo)?.valor_atual ?? null;
  const receitaLiq = calc("RECEITA_LIQUIDA");
  const ebitda = calc("EBITDA");
  const lucro = calc("LUCRO_LIQUIDO");
  const linhasD = useMemo(() => {
    const op = dre.data?.secoes.find((s) => s.nome.toUpperCase().startsWith("OPERACIONAL"));
    // #22 (decisao Veiga): CMV/CPV de energia (D1) FORA — mostra so o
    // operacional que a gestao controla (consistente com o desktop).
    return (op?.linhas ?? []).filter((l) => l.sinal === -1 && l.codigo !== "D1");
  }, [dre.data]);
  const despesasTotal = linhasD.reduce((s, l) => s + Math.abs(l.valor_atual), 0);
  const maiorDespesa = linhasD.reduce(
    (m, l) => (Math.abs(l.valor_atual) > Math.abs(m?.valor_atual ?? 0) ? l : m),
    null as (typeof linhasD)[number] | null,
  );

  // ---- A receber ----
  const cron = cr.data?.cronograma ?? null;

  // ---- Exposição ----
  const expo = exposicao.data;
  const maiorExpo = useMemo(() => {
    if (!expo?.linhas?.length) return null;
    return [...expo.linhas].sort(
      (a, b) => Math.abs(b.mtm_liquido) - Math.abs(a.mtm_liquido),
    )[0];
  }, [expo]);

  // ---- PLD (mês corrente, SE) ----
  const pldDias = pld.data?.dias ?? [];
  const realizados = pldDias.filter((d) => d.realizado && d.pld_projetado != null);
  const pldMtd = realizados.length
    ? realizados.reduce((s, d) => s + (d.pld_projetado as number), 0) / realizados.length
    : null;
  const pldFech = pld.data?.fechamento_projetado ?? null;

  return (
    <div className="min-h-screen bg-indra-azul" data-testid="mobile-resumo">
      <MobileHeader subtitle="Resumo" onLogout={onLogout} onRefresh={onRefresh} />

      <main className="px-4 py-4 flex flex-col gap-4 max-w-md mx-auto">
        {/* SALDO ATUAL — disclosure tocável abre o por-banco (hardening A2) */}
        <Card title="Saldo em conta" testId="mobile-card-saldo">
          <button
            type="button"
            onClick={() => setSaldoAberto((v) => !v)}
            aria-expanded={saldoAberto}
            aria-controls="mobile-saldo-bancos"
            data-testid="mobile-saldo-toggle"
            className="w-full min-h-11 flex items-center justify-between gap-2 text-left focus:outline-none focus:ring-2 focus:ring-indra-turquesa/40 rounded-lg"
          >
            <div className="text-3xl font-black font-sans text-white whitespace-nowrap tabular-nums" data-testid="mobile-saldo-valor">
              {saldoV2.isLoading && !saldoV2.data
                ? "…"
                : saldoReal !== null
                  ? fmtBRL(saldoReal, MONEY_ABBR)
                  : "—" /* erro sem dado: nunca mostrar o mock como saldo */}
            </div>
            <ChevronDown
              size={18}
              className={"text-white/60 transition-transform " + (saldoAberto ? "rotate-180" : "")}
            />
          </button>
          {saldoV2.error && !saldoV2.data && <ErroNote testId="mobile-saldo-erro" />}
          <div className="text-white/60 text-xs mt-1">
            Disponível em conta · toque para ver por banco.
          </div>
          {saldoAberto && (
            <div id="mobile-saldo-bancos" className="mt-2 pt-2 border-t border-white/10 flex flex-col gap-1.5">
              {contasMonitoradas.length === 0 ? (
                <div className="text-white/60 text-sm">—</div>
              ) : (
                contasMonitoradas.map((c) => (
                  <div key={`${c.banco_nome}-${c.conta ?? ""}`} className="flex items-center justify-between gap-2" data-testid="mobile-saldo-banco">
                    <span className="text-sm text-slate-200 truncate">{c.banco_nome}</span>
                    <span className="text-sm font-mono tabular-nums text-slate-100 whitespace-nowrap flex-shrink-0">
                      {c.saldo_atual !== null ? fmtBRL(c.saldo_atual, MONEY) : "—"}
                    </span>
                  </div>
                ))
              )}
              {nomesNa.length > 0 && (
                <div className="text-xs text-white/60 pt-1" data-testid="mobile-saldo-na">
                  Sem dados: {nomesNa.join(", ")}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* FLUXO 30D — tocável (tela mobile existe) */}
        <Card
          title="Fluxo — próximos 30 dias"
          testId="mobile-card-fluxo30d"
          onPress={onNavigate ? () => onNavigate("fluxo") : undefined}
          pressHint="abrir Fluxo"
        >
          {((fluxoV2.error && !fluxoV2.data) || (saldoV2.error && !saldoV2.data)) && (
            <ErroNote testId="mobile-fluxo30d-erro" />
          )}
          <Stat label="Menor saldo projetado" small
            tone={menorSaldo30d?.abaixoPiso ? "neg" : "neutro"}
            value={menorSaldo30d
              ? fmtBRL(menorSaldo30d.valor, MONEY_ABBR)
              : fluxoV2.isLoading || saldoV2.isLoading ? "…" : "—"}
            testId="mobile-fluxo30d-menor" />
          <Stat label="Saldo pós-6DU" small tone={(saldoPos6du ?? 0) >= 0 ? "turq" : "neg"}
            value={saldoPos6du !== null && temAlgum6du ? fmtBRL(saldoPos6du, MONEY_ABBR) : "—"}
            testId="mobile-fluxo30d-pos6du" />
        </Card>

        {/* PRÓXIMO 6DU — tocável (tela mobile existe) */}
        <Card
          title="Próximo 6DU — Liquidação CCEE"
          testId="mobile-card-6du"
          onPress={onNavigate ? () => onNavigate("fluxo") : undefined}
          pressHint="abrir Fluxo"
        >
          <div className="text-xl font-black text-white mb-1" data-testid="mobile-6du-data">
            {officialDate ? formatarDataLongaBr(officialDate) : "—"}
          </div>
          {fluxoRows.erro && <ErroNote testId="mobile-6du-erro" />}
          <Stat label="Entradas previstas" tone="pos" small
            value={temDados6du ? fmtBRL(kpis6du!.entradas, MONEY_ABBR) : "—"} testId="mobile-6du-entradas" />
          <Stat label="Saídas previstas" tone="neg" small
            value={temDados6du ? fmtBRL(kpis6du!.saidas, MONEY_ABBR) : "—"} testId="mobile-6du-saidas" />
          <Stat label="Posição aberta (est.)" tone={posValor >= 0 ? "pos" : "neg"} small
            value={posTemDados ? `${sign(posValor)}${fmtBRL(posValor, MONEY_ABBR)}` : "—"} testId="mobile-6du-posicao" />
          <div className="border-t border-white/10 mt-1 pt-1">
            <Stat label="Saldo total estimado" tone={saldoTotal6du >= 0 ? "turq" : "neg"}
              value={temAlgum6du ? `${sign(saldoTotal6du)}${fmtBRL(saldoTotal6du, MONEY_ABBR)}` : "—"} testId="mobile-6du-saldo-total" />
          </div>
          {receb6du.erro ? (
            <ErroNote testId="mobile-6du-receb-erro" />
          ) : (
            <div className="text-xs text-white/60 mt-1.5" data-testid="mobile-6du-receb">
              {receb6du.data
                ? `A receber no 6DU: ${fmtBRL(receb6du.data.total_esperado, MONEY_ABBR)} de ${receb6du.data.n_contrapartes} contrapartes`
                : receb6du.loading ? "…" : "—"}
            </div>
          )}
        </Card>

        {/* CAIXA NO MÊS — realizado × projetado (visão caixa; o DRE abaixo é
            competência). Mesmo fetch do fluxo30d (useFluxoV2), zero custo. */}
        <Card title="Caixa no mês (realizado + projetado)" testId="mobile-card-mes">
          {fluxoV2.error && !fluxoV2.data && <ErroNote testId="mobile-mes-erro" />}
          <Stat label="Entradas realizadas" tone="pos" small
            value={fluxoV2.data ? fmtBRL(fluxoV2.data.realizado_entradas_mes, MONEY_ABBR) : fluxoV2.isLoading ? "…" : "—"}
            testId="mobile-mes-ent-real" />
          <Stat label="Entradas projetadas" tone="pos" small
            value={fluxoV2.data ? fmtBRL(fluxoV2.data.projetado_entradas_mes, MONEY_ABBR) : fluxoV2.isLoading ? "…" : "—"}
            testId="mobile-mes-ent-proj" />
          <Stat label="Saídas realizadas" tone="neg" small
            value={fluxoV2.data ? fmtBRL(fluxoV2.data.realizado_saidas_mes, MONEY_ABBR) : fluxoV2.isLoading ? "…" : "—"}
            testId="mobile-mes-sai-real" />
          <Stat label="Saídas projetadas" tone="neg" small
            value={fluxoV2.data ? fmtBRL(fluxoV2.data.projetado_saidas_mes, MONEY_ABBR) : fluxoV2.isLoading ? "…" : "—"}
            testId="mobile-mes-sai-proj" />
        </Card>

        {/* RESULTADO DO MÊS (DRE gerencial) — read-only, drill = Fase 2 */}
        <Card title="Resultado do mês (DRE)" testId="mobile-card-resultado">
          {dre.error && !dre.data && <ErroNote testId="mobile-resultado-erro" />}
          <Stat label="Receita líquida" tone="pos" small
            value={receitaLiq != null ? fmtBRL(receitaLiq, MONEY_ABBR) : dre.isLoading ? "…" : "—"}
            testId="mobile-resultado-receita" />
          <Stat label="EBITDA" tone={ebitda != null && ebitda < 0 ? "neg" : "neutro"} small
            value={ebitda != null ? fmtBRL(ebitda, MONEY_ABBR) : dre.isLoading ? "…" : "—"}
            testId="mobile-resultado-ebitda" />
          <Stat label="Lucro líquido" tone={lucro != null && lucro < 0 ? "neg" : "turq"} small
            value={lucro != null ? fmtBRL(lucro, MONEY_ABBR) : dre.isLoading ? "…" : "—"}
            testId="mobile-resultado-lucro" />
        </Card>

        {/* DESPESAS DO MÊS — read-only, drill = Fase 2 */}
        <Card title="Despesas operacionais do mês" testId="mobile-card-despesas">
          {dre.error && !dre.data && <ErroNote testId="mobile-despesas-erro" />}
          <Stat label="Total" tone="neg" small
            value={linhasD.length ? fmtBRL(despesasTotal, MONEY_ABBR) : dre.isLoading ? "…" : "—"}
            testId="mobile-despesas-total" />
          <div className="text-xs text-white/60 mt-1" data-testid="mobile-despesas-maior">
            {maiorDespesa
              ? `Maior linha: ${maiorDespesa.nome} (${fmtBRL(Math.abs(maiorDespesa.valor_atual), MONEY_ABBR)})`
              : "—"}
          </div>
          <div className="text-[11px] text-white/60 mt-0.5">
            Sem CMV/CPV de energia (D1) — visão do que a gestão controla.
          </div>
        </Card>

        {/* A RECEBER — read-only, drill = Fase 2 */}
        <Card title="A receber (energia) · por vencimento" testId="mobile-card-receber">
          {cr.error && !cr.data && <ErroNote testId="mobile-receber-erro" />}
          <Stat label="Total em aberto" tone="pos"
            value={cr.data ? fmtBRL(cr.data.total_a_receber, MONEY_ABBR) : cr.isLoading ? "…" : "—"}
            testId="mobile-receber-total" />
          {cron && (
            <>
            <div className="text-[11px] text-white/60 mt-0.5">
              Janelas por data de vencimento (a vencer) — não é atraso.
            </div>
            <div className="grid grid-cols-4 gap-1 mt-1 text-center" data-testid="mobile-receber-aging">
              {[
                ["0-30d", cron.d0_30],
                ["31-60d", cron.d31_60],
                ["61-90d", cron.d61_90],
                ["90d+", cron.d90_mais],
              ].map(([label, v]) => (
                <div key={label as string} className="rounded-md bg-white/5 py-1.5">
                  <div className="text-[11px] text-white/60">{label}</div>
                  <div className="text-xs font-mono tabular-nums text-slate-100">
                    {fmtBRL(v as number, MONEY_ABBR)}
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </Card>

        {/* POSIÇÃO / EXPOSIÇÃO — read-only, drill = Fase 2 */}
        <Card title="Exposição MtM (contrapartes)" testId="mobile-card-exposicao">
          {exposicao.erro && <ErroNote testId="mobile-exposicao-erro" />}
          <Stat label="MtM líquido total" tone={(expo?.mtm_liquido_total ?? 0) >= 0 ? "pos" : "neg"} small
            value={expo ? fmtBRL(expo.mtm_liquido_total, MONEY_ABBR) : exposicao.loading ? "…" : "—"}
            testId="mobile-exposicao-mtm" />
          <div className="text-xs text-white/60 mt-1" data-testid="mobile-exposicao-maior">
            {maiorExpo
              ? `Maior: ${maiorExpo.contraparte} (${fmtBRL(maiorExpo.mtm_liquido, MONEY_ABBR)})`
              : "—"}
          </div>
        </Card>

        {/* PLD — tocável (tela mobile existe) */}
        <Card
          title={`PLD SE — ${mesLabel(mesRefCorrente())}`}
          testId="mobile-card-pld"
          onPress={onNavigate ? () => onNavigate("pld") : undefined}
          pressHint="abrir PLD"
        >
          {pld.error && !pld.data && <ErroNote testId="mobile-pld-erro" />}
          <Stat label="Realizado (MTD)" small value={pldMtd != null ? fmtPld(pldMtd) : "—"} testId="mobile-pld-mtd" />
          <Stat label="Projetado (fechamento)" tone="turq"
            value={pldFech != null ? fmtPld(pldFech) : "—"} testId="mobile-pld-fechamento" />
          <div className="text-white/60 text-xs mt-1" data-testid="mobile-pld-banda">
            {pld.data?.fechamento_p10 != null && pld.data?.fechamento_p90 != null
              ? `Banda P10–P90: ${fmtPld(pld.data.fechamento_p10)} – ${fmtPld(pld.data.fechamento_p90)}`
              : "—"}
          </div>
        </Card>

        <p className="text-center text-white/40 text-xs py-2">
          Detalhe completo no desktop.
        </p>
      </main>
    </div>
  );
}
